<template>
  <form @submit.prevent="submit">
    <FormInput
      name="hostname"
      v-model="fields.hostname"
      testId="hostname-input"
      :label="`${$t('global.hostname')} *`"
      :errors="validation.fields.hostname.errors"
      :dirty="validation.fields.hostname.dirty"
      :disabled="isSubmitting"
      class="pb-4"
    />
    <FormInput
      name="aliasTo"
      v-model="fields.aliasTo"
      testId="alias-to-input"
      :label="`${$t('global.alias-to')} *`"
      :errors="validation.fields.aliasTo.errors"
      :dirty="validation.fields.aliasTo.dirty"
      :disabled="isSubmitting"
      class="pb-4"
    />
    <FormInput
      name="ttl"
      v-model="fields.ttl"
      testId="ttl-input"
      :label="`${$t('global.ttl')} *`"
      :errors="validation.fields.ttl.errors"
      :dirty="validation.fields.ttl.dirty"
      :disabled="isSubmitting"
      class="pb-4"
    />
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-add-dns-zone-cname-record-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="add-dns-zone-cname-record-button"
      >
        {{ $t(editing ? "global.save" : "global.add") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormInput from "@/components/display/form/FormInput";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { addDnsZoneCnameRecord as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "DnsZoneCNameRecordForm",
  props: {
    aliasTo: String,
    recordId: Number,
    dnsZone: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    hostname: String,
    ttl: {
      type: Number,
      default: 300,
    },
  },
  components: {
    Btn,
    FormInput,
  },
  setup(props, { emit, root }) {
    const isSubmitting = ref(false);
    const errors = ref(null);
    const fields = reactive({
      aliasTo: props.aliasTo,
      hostname: props.hostname,
      ttl: props.ttl,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        aliasTo: {
          required: root.$t("validation.type-required"),
        },
        hostname: {
          dnsDomainName: root.$t("validation.hostname-invalid"),
          required: root.$t("validation.hostname-required"),
        },
        ttl: {
          regex: root.$t("validation.ttl-number"),
          required: root.$t("validation.ttl-required"),
        },
      }
    );

    const cancel = () => emit("close");

    const isSubmittable = () =>
      !isSubmitting.value &&
      validation.errors.length === 0 &&
      (fields.aliasTo !== props.aliasTo ||
        fields.hostname !== props.hostname ||
        fields.ttl !== props.ttl);

    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          isSubmitting.value = true;

          const { aliasTo, hostname, ttl } = fields;
          const action = props.editing ? "dnsZoneCnameRecords/update" : "dnsZoneCnameRecords/add";

          store
            .dispatch(action, {
              aliasTo,
              dnsZoneId: props.dnsZone.id,
              hostname,
              recordId: props.recordId,
              ttl,
            })
            .then(() => {
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.dns-zone-cname-record-message", {
                  action: props.editing ? "edited" : "added",
                  record: hostname,
                  zone: props.dnsZone.domain,
                }),
              });
              emit("close");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.dns-zone-cname-record-error-message", {
                  action: props.editing ? "editing" : "adding",
                  record: hostname,
                  zone: props.dnsZone.domain,
                }),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };

    return {
      cancel,
      errors,
      fields,
      isSubmittable,
      isSubmitting,
      submit,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
